"use client";

import { useCallback, useMemo, useState } from "react";
import Link from "next/link";
import { GoogleMap, Polygon, useJsApiLoader } from "@react-google-maps/api";
import ChevronRightIcon from "@/components/icons/ChevronRightIcon";
import LogoLoader from "@/components/ui/LogoLoader";
import { Button } from "@/components";

const GOOGLE_MAPS_API_KEY =
  process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY ||
  "AIzaSyDiYGP4ZBJZPXU0OJoJ3y764FfKyWZ9mQc";

const ALBERTA_CENTER = { lat: 54.5, lng: -115.0 };
const ALBERTA_ZOOM = 5;

const DARK_MAP_STYLES: google.maps.MapTypeStyle[] = [
  { elementType: "geometry", stylers: [{ color: "#1a1d3d" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#8a90b8" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#151835" }, { weight: 2 }] },
  { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { featureType: "administrative", elementType: "geometry.stroke", stylers: [{ color: "#2d3460" }] },
  { featureType: "administrative.province", elementType: "geometry.stroke", stylers: [{ color: "#4a5590" }, { weight: 2 }] },
  { featureType: "administrative.province", elementType: "geometry.fill", stylers: [{ color: "#242855" }] },
  { featureType: "administrative.country", elementType: "geometry.stroke", stylers: [{ color: "#4a5590" }, { weight: 1.5 }] },
  { featureType: "administrative.locality", elementType: "labels.text.fill", stylers: [{ color: "#a0a5cc" }] },
  { featureType: "administrative.land_parcel", stylers: [{ visibility: "off" }] },
  { featureType: "landscape", elementType: "geometry", stylers: [{ color: "#1e2245" }] },
  { featureType: "landscape.natural", elementType: "geometry", stylers: [{ color: "#1e2245" }] },
  { featureType: "landscape.natural.terrain", elementType: "geometry", stylers: [{ color: "#262b58" }] },
  { featureType: "landscape.natural.landcover", elementType: "geometry", stylers: [{ color: "#212650" }] },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "poi.park", stylers: [{ visibility: "on" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#1c2848" }] },
  { featureType: "poi.park", elementType: "labels.text.fill", stylers: [{ color: "#6b7094" }] },
  { featureType: "poi.park", elementType: "labels.text.stroke", stylers: [{ color: "#151835" }] },
  { featureType: "road", stylers: [{ visibility: "off" }] },
  { featureType: "road.highway", stylers: [{ visibility: "simplified" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#2a3060" }, { weight: 0.5 }] },
  { featureType: "road.highway", elementType: "labels", stylers: [{ visibility: "off" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#121535" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#3a4070" }] },
  { featureType: "water", elementType: "labels.text.stroke", stylers: [{ color: "#0e1028" }] },
];

const MAP_OPTIONS: google.maps.MapOptions = {
  disableDefaultUI: true,
  zoomControl: false,
  gestureHandling: "cooperative",
  styles: DARK_MAP_STYLES,
  backgroundColor: "#1a1d3d",
  minZoom: 4,
  maxZoom: 8,
  mapTypeId: "terrain",
};

const containerStyle = { width: "100%", height: "100%" };

const ALBERTA_BOUNDARY: google.maps.LatLngLiteral[] = [
  { lat: 60.0, lng: -120.0 },
  { lat: 56.0, lng: -120.0 },
  { lat: 54.0, lng: -120.0 },
  { lat: 53.8, lng: -119.8 },
  { lat: 53.4, lng: -119.2 },
  { lat: 53.0, lng: -118.8 },
  { lat: 52.6, lng: -118.3 },
  { lat: 52.2, lng: -117.8 },
  { lat: 51.8, lng: -117.4 },
  { lat: 51.4, lng: -117.1 },
  { lat: 51.1, lng: -116.8 },
  { lat: 50.7, lng: -116.2 },
  { lat: 50.4, lng: -115.8 },
  { lat: 50.0, lng: -115.3 },
  { lat: 49.6, lng: -114.8 },
  { lat: 49.2, lng: -114.2 },
  { lat: 49.0, lng: -114.05 },
  { lat: 49.0, lng: -112.0 },
  { lat: 49.0, lng: -110.0 },
  { lat: 52.0, lng: -110.0 },
  { lat: 54.0, lng: -110.0 },
  { lat: 56.0, lng: -110.0 },
  { lat: 58.0, lng: -110.0 },
  { lat: 60.0, lng: -110.0 },
  { lat: 60.0, lng: -114.0 },
  { lat: 60.0, lng: -120.0 },
];

const ALBERTA_POLYGON_OPTIONS: google.maps.PolygonOptions = {
  fillColor: "#3b5cff",
  fillOpacity: 0.15,
  strokeColor: "#5575ff",
  strokeOpacity: 0.5,
  strokeWeight: 2,
};

type Props = {
  city?: string | null;
};

function AlbertaGoogleMap() {
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script-dispensary-locations",
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: ["places"] as any,
  });

  const onLoad = useCallback((map: google.maps.Map) => {
    const albertaBounds = new google.maps.LatLngBounds(
      { lat: 49.0, lng: -120.0 },
      { lat: 60.0, lng: -110.0 }
    );
    map.fitBounds(albertaBounds, 20);
  }, []);

  if (!isLoaded) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-[#1a1d3d]">
        <LogoLoader size="md" label="Loading map" />
      </div>
    );
  }

  return (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={ALBERTA_CENTER}
      zoom={ALBERTA_ZOOM}
      options={MAP_OPTIONS}
      onLoad={onLoad}
    >
      <Polygon paths={ALBERTA_BOUNDARY} options={ALBERTA_POLYGON_OPTIONS} />
    </GoogleMap>
  );
}

export default function LocationCityDeliverySection({ city }: Props) {
  const [tab, setTab] = useState<"about" | "delivery">("about");
  const cityName = city?.trim() || "Calgary";

  const content = useMemo(
    () => ({
      about: {
        tabLabel: `About ${cityName}`,
        heading: `About ${cityName}`,
        paragraphs: [
          `When you think of ${cityName}, what comes to mind? For most shoppers it's a city with its own pace, personality, and neighbourhood character. That mix of local culture and convenience is what makes the cannabis experience here feel different.`,
          `Dank is part of that experience. We focus on curated products, friendly service, and a store visit that feels easy from the moment you walk in. Shopping for your usuals or trying something new, ${cityName} is a great place to do it.`,
        ],
      },
      delivery: {
        tabLabel: "Where do we deliver",
        heading: "Where do we deliver?",
        paragraphs: [
          `We serve shoppers throughout ${cityName} and surrounding Alberta communities with fast fulfillment options based on your address. Start your order online and we will surface the most convenient option available to you.`,
          "If same-day delivery is available for your area, you will see it during the shopping flow. Outside local delivery zones, Alberta-wide mail order helps keep the full Dank menu within reach.",
        ],
      },
    }),
    [cityName]
  );

  const activeContent = tab === "about" ? content.about : content.delivery;

  return (
    <section className="w-full max-w-none" aria-label={`${cityName} and delivery area`}>
      <div className="grid w-full gap-0 overflow-hidden rounded-2xl border border-neutral-10 lg:grid-cols-2">
        <div className="relative min-h-[300px] bg-[#1a1d3d] lg:min-h-[460px]">
          <AlbertaGoogleMap />
        </div>

        <div className="flex flex-col bg-constant-white">
          <div className="flex flex-col gap-2 border-b border-neutral-10 p-3 sm:flex-row sm:gap-1 sm:p-2">
            <Button
              variant={ tab === "about" ? "primary" : "bordered" }
              size="lg"
              onClick={() => setTab('about')}
              className="w-full rounded-xl"
            >
              {content.about.tabLabel}
            </Button>
            <Button
              variant={ tab === "delivery" ? "primary" : "bordered" }
              size="lg"
              onClick={() => setTab('delivery')}
              className="w-full rounded-xl"
            >
              {content.delivery.tabLabel}
            </Button>
          </div>

          <div className="relative flex-1 overflow-y-auto p-5 sm:p-6">
            <h2 className="text-h3-mobile font-bold text-text-primary">
              {activeContent.heading}
            </h2>
            <div className="mt-4 space-y-4 text-body-md font-medium leading-relaxed text-text-secondary">
              {activeContent.paragraphs.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </div>
            <div
              className="pointer-events-none sticky bottom-0 left-0 right-0 mt-4 h-12 bg-gradient-to-t from-constant-white to-transparent"
              aria-hidden
            />
          </div>

          <div className="grid grid-cols-1 divide-y divide-neutral-10 border-t border-neutral-10 sm:grid-cols-2 sm:divide-x sm:divide-y-0">
            <Link
              href="/search"
              className="flex items-center justify-between gap-3 px-4 py-4 text-body-md font-semibold text-blue-80 transition-colors sm:justify-center sm:gap-1.5"
            >
              <span>Shop same-day order</span>
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-blue-10 bg-blue-5 sm:hidden">
                <ChevronRightIcon size={14} className="text-blue-80" />
              </span>
              <ChevronRightIcon size={14} className="hidden text-blue-80 sm:inline" />
            </Link>
            <Link
              href="/account"
              className="flex items-center justify-between gap-3 px-4 py-4 text-body-md font-semibold text-blue-80 transition-colors sm:justify-center sm:gap-1.5"
            >
              <span>Join the Dank club</span>
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-blue-10 bg-blue-5 sm:hidden">
                <ChevronRightIcon size={14} className="text-blue-80" />
              </span>
              <ChevronRightIcon size={14} className="hidden text-blue-80 sm:inline" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
